/**
 * The call — a Decision read as one sentence with nine slots (owner ruling,
 * Wes, 2026-10-28; the installed-offense vocabulary, v2 §2 and §7).
 *
 * A record holds when anyone, at any level, any later time, reads the same
 * consequential meaning from it without the people who made it. A call is a
 * record complete enough for that: nine slots, each answering one question a
 * later reader asks. A slot the record cannot fill is shown as a blank, never
 * filled in (canon 02B L1: "no basis", never a smoothed guess).
 *
 * Pure: takes gathered facts, returns the slots, the sentence and the count.
 * The gathering lives in callService.ts; this file never touches the network.
 */

import { formatDecisionDate } from '@/components/prioritization/decisions/decisionRecordText';

export type CallSlotKey =
  | 'subject' | 'act' | 'standing' | 'authority' | 'frame' | 'basis' | 'relations' | 'lineage' | 'time';

export interface CallSlot {
  key: CallSlotKey;
  /** The slot's name on the wristband. */
  label: string;
  /** The reader's question this slot answers. */
  question: string;
  /** What the record says, or null when the record is silent. */
  value: string | null;
  /** A second line: where the value came from, or why it is blank. */
  detail?: string | null;
}

export interface CallFrame {
  sessionName?: string | null;
  intent?: string | null;
  criteria?: string[];
  poolSize?: number | null;
  criteriaLockedAt?: string | null;
  cycleName?: string | null;
  /** A plan authorization's frame, when the act was about a plan. */
  plan?: CallPlanFrame | null;
}

/** What a plan was judged under: what it planned from, its horizon, when it takes effect. */
export interface CallPlanFrame {
  /** planning_input_type: published_priority_list, item_pool, collection, previous_carry_forward. */
  inputKind?: string | null;
  inputName?: string | null;
  /** When the input was taken (plan_cycles.input_snapshot_at). */
  inputAsOf?: string | null;
  horizon?: string | null;
  takesEffectOn?: string | null;
}

export interface CallElement { kind: string; statement: string; status: string }
export interface CallRelation { type: string; subtype?: string | null; direction: 'out' | 'in'; otherName: string }

export interface CallFacts {
  decidedAt: string;
  decidedByName?: string | null;
  roleAtAct?: string[] | null;
  rightAtAct?: string | null;
  source?: string | null;
  decisionKind: 'manual' | 'auto';
  decisionClass?: string | null;
  title: string;
  subjectName?: string | null;
  subjectKind?: string | null;
  relatedNames?: string[];
  status: 'active' | 'superseded' | 'reversed';
  reviewByDate?: string | null;
  reconsiderationConditions?: string | null;
  frame?: CallFrame | null;
  rationaleReasoning?: string | null;
  rationaleContext?: string | null;
  rationaleAlternatives?: string | null;
  body?: string | null;
  elements?: CallElement[];
  /** null = the subject's relations could not be read; [] = none declared. */
  relations?: CallRelation[] | null;
  supersedesTitle?: string | null;
  supersededByTitle?: string | null;
  parentTitle?: string | null;
  /** ISO date for "today", injectable for tests. */
  today?: string;
}

export interface Call {
  slots: CallSlot[];
  sentence: string;
  filled: number;
  whole: boolean;
  lapsed: boolean;
  /** A whole call at the top of its ladder, reached by ceremony (v3: Agreement). */
  agreement: boolean;
  actWord: string;
}

export const CALL_SLOT_QUESTIONS: Record<CallSlotKey, { label: string; question: string }> = {
  subject:   { label: 'Subject',   question: 'What is this about?' },
  act:       { label: 'Act',       question: 'What was done?' },
  standing:  { label: 'Standing',  question: 'How settled is it now?' },
  authority: { label: 'Authority', question: 'Who did it, and could they?' },
  frame:     { label: 'Frame',     question: 'Under what was it judged?' },
  basis:     { label: 'Basis',     question: 'Why?' },
  relations: { label: 'Relations', question: 'What else did it touch?' },
  lineage:   { label: 'Lineage',   question: 'What did it replace, what came from it?' },
  time:      { label: 'Time',      question: 'When, until when, what lapses it?' },
};

const CEREMONY_ACTS = new Set(['ratified', 'published', 'authorized', 'accepted']);

/** The act's own word, from the source that wrote the Decision. Never "decided". */
export function actWordFor(source: string | null | undefined, decisionKind: 'manual' | 'auto'): string {
  const s = (source ?? '').toLowerCase();
  const table: Array<[string, string]> = [
    ['authoriz', 'authorized'], ['accept', 'accepted'], ['ratif', 'ratified'], ['publish', 'published'],
    ['lock', 'locked'], ['claim', 'claimed'], ['arm', 'armed'], ['supersed', 'superseded'],
    ['reopen', 'reopened'], ['overrid', 'overrode'], ['override', 'overrode'], ['withdraw', 'withdrew'],
    ['contest', 'contested'], ['readback', 'read back'], ['dispose', 'disposed of'], ['triage', 'triaged'],
    ['propos', 'proposed'], ['reject', 'rejected'], ['revis', 'revised'], ['acknowledg', 'acknowledged'],
  ];
  for (const [needle, word] of table) if (s.includes(needle)) return word;
  return decisionKind === 'auto' ? 'recorded automatically' : 'recorded';
}

function humanRole(roles: string[] | null | undefined): string | null {
  if (!roles || roles.length === 0) return null;
  const pick = roles.find((r) => r.startsWith('role:')) ?? roles.find((r) => r.startsWith('workspace:')) ?? roles[0];
  if (pick.startsWith('role:')) return pick.slice(5);
  if (pick.startsWith('workspace:')) return `workspace ${pick.slice(10)}`;
  if (pick.startsWith('platform:')) return pick.slice(9).replace(/_/g, ' ');
  return pick;
}

function blank(key: CallSlotKey): string {
  return `[${CALL_SLOT_QUESTIONS[key].label.toLowerCase()}: not on the record]`;
}

const PLAN_INPUT_WORDS: Record<string, string> = {
  published_priority_list: 'published priority list',
  item_pool: 'item pool',
  collection: 'collection',
};

/**
 * The frame of a plan authorization: what the plan planned from (its input,
 * as of when it was taken), its horizon and when it takes effect. Null when
 * the record names none of them.
 */
export function planFramePhrase(p: CallPlanFrame): string | null {
  const parts: string[] = [];
  const named = p.inputName?.trim() ? `"${p.inputName.trim()}"` : null;
  let input: string | null = null;
  if (p.inputKind === 'previous_carry_forward') {
    input = named ? `the carry-forward from the plan cycle ${named}` : 'the previous plan cycle\'s carry-forward';
  } else if (p.inputKind || named) {
    const kind = p.inputKind ? (PLAN_INPUT_WORDS[p.inputKind] ?? p.inputKind.replace(/_/g, ' ')) : 'input';
    input = named ? `the ${kind} ${named}` : `${/^[aeiou]/i.test(kind) ? 'an' : 'a'} ${kind}`;
  }
  if (input) parts.push(`${input} as the plan's input${p.inputAsOf ? ` (as of ${formatDecisionDate(p.inputAsOf)})` : ''}`);
  if (p.horizon?.trim()) parts.push(`horizon ${p.horizon.trim()}`);
  if (p.takesEffectOn) parts.push(`in effect from ${formatDecisionDate(p.takesEffectOn)}`);
  return parts.length ? parts.join('; ') : null;
}

function relationPhrase(r: CallRelation): string {
  const t = r.type.replace(/_/g, ' ');
  const sub = r.subtype ? ` (${r.subtype.replace(/_/g, ' ')})` : '';
  return r.direction === 'out' ? `${t} ${r.otherName}${sub}` : `${r.otherName} ${t} this${sub}`;
}

export function buildCall(f: CallFacts): Call {
  const today = f.today ?? new Date().toISOString().slice(0, 10);
  const actWord = actWordFor(f.source, f.decisionKind);

  // 1. Subject
  const subjectValue = f.subjectName?.trim() || (f.relatedNames && f.relatedNames[0]) || null;
  const subject: CallSlot = {
    key: 'subject', ...CALL_SLOT_QUESTIONS.subject,
    value: subjectValue,
    detail: subjectValue
      ? (f.subjectKind ? `subject kind: ${f.subjectKind.replace(/_/g, ' ')}` : null)
      : 'No item or related object is named on this record.',
  };

  // 2. Act
  const act: CallSlot = {
    key: 'act', ...CALL_SLOT_QUESTIONS.act,
    value: actWord,
    detail: f.decisionClass ? `class: ${f.decisionClass.replace(/_/g, ' ')}` : (f.source ? `written by ${f.source}` : null),
  };

  // 3. Standing (with lapse)
  const lapsed = f.status === 'active' && !!f.reviewByDate && f.reviewByDate.slice(0, 10) < today;
  const standingWord = f.status === 'active' ? (lapsed ? `active, lapsed since ${(f.reviewByDate ? formatDecisionDate(f.reviewByDate) : null)}` : 'active') : f.status;
  const standing: CallSlot = {
    key: 'standing', ...CALL_SLOT_QUESTIONS.standing,
    value: standingWord,
    detail: lapsed ? 'The review date passed with no renewal; the record still says active.' : null,
  };

  // 4. Authority
  const role = humanRole(f.roleAtAct);
  const name = f.decidedByName?.trim() || null;
  const authority: CallSlot = {
    key: 'authority', ...CALL_SLOT_QUESTIONS.authority,
    value: name ? (role ? `${name} as ${role}` : `${name} (role at the time not on the record)`) : null,
    detail: f.rightAtAct ? `allowed on: ${f.rightAtAct.replace(/_/g, ' ')}` : (name && !role ? 'Older records carry no role-at-act stamp (migration 959 added it).' : null),
  };

  // 5. Frame
  let frameValue: string | null = null;
  if (f.frame?.sessionName) {
    const parts: string[] = [`the session "${f.frame.sessionName}"`];
    if (f.frame.intent) parts.push(`intent: ${f.frame.intent}`);
    if (f.frame.criteria && f.frame.criteria.length) parts.push(`criteria: ${f.frame.criteria.join(', ')}`);
    if (typeof f.frame.poolSize === 'number') parts.push(`pool of ${f.frame.poolSize}`);
    if (f.frame.criteriaLockedAt) parts.push(`criteria locked ${(f.frame.criteriaLockedAt ? formatDecisionDate(f.frame.criteriaLockedAt) : null)}`);
    frameValue = parts.join('; ');
  } else if (f.frame?.plan && planFramePhrase(f.frame.plan)) {
    frameValue = planFramePhrase(f.frame.plan);
  } else if (f.frame?.cycleName) {
    frameValue = `the cycle "${f.frame.cycleName}" (no session frame on the record)`;
  }
  const frame: CallSlot = {
    key: 'frame', ...CALL_SLOT_QUESTIONS.frame,
    value: frameValue,
    detail: frameValue
      ? (f.frame?.plan && !f.frame.sessionName ? 'Read from the plan cycle: what it planned from, its horizon and when it takes effect.' : null)
      : 'No session, plan or cycle is named, so what it was judged under is not on the record.',
  };

  // 6. Basis
  const why = f.rationaleReasoning?.trim() || f.body?.trim() || f.rationaleContext?.trim() || null;
  const els = f.elements ?? [];
  const holding = els.filter((e) => e.status === 'holding' || e.status === 'active').length;
  const basisValue = why
    ? (els.length ? `${why} (resting on ${els.length} condition${els.length === 1 ? '' : 's'}, ${holding} holding)` : why)
    : (els.length ? `resting on ${els.length} condition${els.length === 1 ? '' : 's'}, ${holding} holding; no reasoning written` : null);
  const basis: CallSlot = {
    key: 'basis', ...CALL_SLOT_QUESTIONS.basis,
    value: basisValue,
    detail: basisValue
      ? (f.rationaleAlternatives ? `alternatives considered: ${f.rationaleAlternatives}` : null)
      : (f.decisionKind === 'auto'
          ? 'No one wrote a reason when this was recorded automatically.'
          : 'No reasoning, context or condition is on the record.'),
  };

  // 7. Relations in force (as they stand now; the record keeps no as-of)
  let relationsValue: string | null = null;
  let relationsDetail: string | null = null;
  if (f.relations === null || f.relations === undefined) {
    relationsDetail = subjectValue ? 'The subject\'s relations could not be read.' : 'No subject item, so nothing to relate.';
  } else if (f.relations.length === 0) {
    relationsValue = 'no relations declared on the subject';
  } else {
    relationsValue = f.relations.map(relationPhrase).join('; ');
    relationsDetail = 'As they stand now; the record keeps no as-of view.';
  }
  const relations: CallSlot = { key: 'relations', ...CALL_SLOT_QUESTIONS.relations, value: relationsValue, detail: relationsDetail };

  // 8. Lineage
  const lin: string[] = [];
  if (f.supersedesTitle) lin.push(`supersedes "${f.supersedesTitle}"`);
  if (f.supersededByTitle) lin.push(`superseded by "${f.supersededByTitle}"`);
  if (f.parentTitle) lin.push(`part of "${f.parentTitle}"`);
  const lineage: CallSlot = {
    key: 'lineage', ...CALL_SLOT_QUESTIONS.lineage,
    value: lin.length ? lin.join('; ') : 'nothing superseded; first of its line',
    detail: null,
  };

  // 9. Time
  const timeParts: string[] = [];
  if (f.reviewByDate) timeParts.push(`until review on ${(f.reviewByDate ? formatDecisionDate(f.reviewByDate) : null)}`);
  if (f.reconsiderationConditions?.trim()) timeParts.push(`revisited when ${f.reconsiderationConditions.trim()}`);
  const time: CallSlot = {
    key: 'time', ...CALL_SLOT_QUESTIONS.time,
    value: timeParts.length ? timeParts.join('; ') : null,
    detail: timeParts.length ? `made ${(f.decidedAt ? formatDecisionDate(f.decidedAt) : null)}` : `Made ${(f.decidedAt ? formatDecisionDate(f.decidedAt) : null)}; no review date and no condition that would lapse it.`,
  };

  const slots = [subject, act, standing, authority, frame, basis, relations, lineage, time];
  const filled = slots.filter((s) => s.value !== null).length;
  const whole = filled === slots.length;

  const sentence = [
    `On ${(f.decidedAt ? formatDecisionDate(f.decidedAt) : null)},`,
    authority.value ?? blank('authority'),
    actWord,
    subject.value ?? blank('subject'),
    `under ${frame.value ?? blank('frame')},`,
    `which is now ${standing.value},`,
    `because ${basis.value ?? blank('basis')},`,
    `with ${relations.value ?? blank('relations')},`,
    `${lineage.value},`,
    `standing ${time.value ?? blank('time')}.`,
  ].join(' ');

  return {
    slots,
    sentence,
    filled,
    whole,
    lapsed,
    agreement: whole && f.status === 'active' && CEREMONY_ACTS.has(actWord),
    actWord,
  };
}
